import { useCallback, useEffect, useRef, useState, type MouseEvent, type RefObject } from "react";

/**
 * **Zwei-Klick-Bestätigung statt `window.confirm()`** — übernommen aus
 * `client/src/react/hooks/use-arm-confirm.ts`. Der erste Klick schärft den
 * Knopf (der Aufrufer färbt ihn und schreibt die Rückfrage hinein), der zweite
 * führt aus. Ein Klick daneben (`mousedown`, Capture-Phase) oder fünf Sekunden
 * nehmen es zurück.
 */
export function useArmConfirm<T extends HTMLElement>(
  onConfirm: () => void | Promise<void>,
  { timeoutMs = 5000 }: { timeoutMs?: number } = {},
): { armed: boolean; ref: RefObject<T | null>; onClick: (e: MouseEvent) => void } {
  const [armed, setArmed] = useState(false);
  const ref = useRef<T | null>(null);
  const run = useRef(onConfirm);
  useEffect(() => { run.current = onConfirm; }, [onConfirm]);

  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), timeoutMs);
    const onOutside = (e: globalThis.MouseEvent) => {
      if (!(e.target instanceof Node) || !ref.current?.contains(e.target)) setArmed(false);
    };
    document.addEventListener("mousedown", onOutside, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onOutside, true);
    };
  }, [armed, timeoutMs]);

  const onClick = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!armed) { setArmed(true); return; }
    setArmed(false);
    void run.current();
  }, [armed]);

  return { armed, ref, onClick };
}
